import React, { useState, useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import SEO from '../../components/SEO';
import { PageHero, Tabs, Icon, Button, EmptyState } from '../../components/ui';

const IMAGES = [
  { src: '/images/design/hero-ram-mandir.webp', title: 'Shri Ram Janmabhoomi Mandir', category: 'ram-mandir' },
  { src: '/images/design/ram-mandir-night.webp', title: 'Ram Mandir illuminated at night', category: 'ram-mandir' },
  { src: '/images/design/ram-mandir-dusk.webp', title: 'The temple complex at dusk', category: 'ram-mandir' },
  { src: '/images/design/ram-mandir-complex.webp', title: 'Ram Mandir complex', category: 'ram-mandir' },
  { src: '/images/ram-mandir/temple.webp', title: 'Nagara-style shikhara', category: 'ram-mandir' },
  { src: '/images/ram-mandir/temple-image.jpeg', title: 'Ram Path approach', category: 'ram-mandir' },
  { src: '/images/ram-lala/ram-lala-01.jpg', title: 'Shri Ram Lalla Virajman', category: 'ram-lalla' },
  { src: '/images/ram-lala/RamImage.jpeg', title: 'Ram Lalla darshan', category: 'ram-lalla' },
  { src: '/images/hanuman/hanuman-grahi-1.jpeg', title: 'Hanuman Garhi gateway', category: 'hanuman-garhi' },
  { src: '/images/hanuman/hanuman-idol.jpeg', title: 'Bal Hanuman idol', category: 'hanuman-garhi' },
  { src: '/images/hanuman/temple-1.webp', title: 'Hanuman Garhi temple', category: 'hanuman-garhi' },
  { src: '/images/hanuman/temple-steps.jpg', title: 'The 76 steps to Hanuman Garhi', category: 'hanuman-garhi' },
  { src: '/images/kanak-bhawan/temple.jpg', title: 'Kanak Bhawan', category: 'kanak-bhawan' },
  { src: '/images/kanak-bhawan/temple-1.jpg', title: 'Kanak Bhawan courtyard', category: 'kanak-bhawan' },
  { src: '/images/kanak-bhawan/rama-1.jpeg', title: 'Ram–Sita shringaar', category: 'kanak-bhawan' },
  { src: '/images/kanak-bhawan/rama-2.jpg', title: 'Golden idols of Kanak Bhawan', category: 'kanak-bhawan' },
  { src: '/images/ghats/saryu-ghat-1.jpeg', title: 'Saryu Ghat', category: 'ghats' },
  { src: '/images/ghats/naya-ghat-2.jpg', title: 'Ram ki Paidi', category: 'ghats' },
  { src: '/images/ghats/guptar-ghat/1.jpg', title: 'Guptar Ghat', category: 'ghats' },
  { src: '/images/ghats/guptar-ghat/2.jpg', title: 'Guptar Ghat steps', category: 'ghats' },
  { src: '/images/ghats/lakshman-ghat-1.jpg', title: 'Lakshman Ghat', category: 'ghats' },
  { src: '/images/ghats/tulsi-ghat-1.jpg', title: 'Tulsi Ghat', category: 'ghats' },
  { src: '/images/ghats/kaushalya-ghat-3.jpg', title: 'Kaushalya Ghat', category: 'ghats' },
  { src: '/images/ghats/gau-ghat-2.jpg', title: 'Gau Ghat', category: 'ghats' },
];

const CATEGORIES = [
  { id: 'all', label: 'All' },
  { id: 'ram-mandir', label: 'Ram Mandir' },
  { id: 'ram-lalla', label: 'Ram Lalla' },
  { id: 'hanuman-garhi', label: 'Hanuman Garhi' },
  { id: 'kanak-bhawan', label: 'Kanak Bhawan' },
  { id: 'ghats', label: 'Ghats' },
];

export default function Gallery() {
  const { t } = useTranslation();
  const [category, setCategory] = useState('all');
  const [openIndex, setOpenIndex] = useState(null);

  const items = category === 'all' ? IMAGES : IMAGES.filter((i) => i.category === category);

  const prev = useCallback(() => setOpenIndex((i) => (i === 0 ? items.length - 1 : i - 1)), [items.length]);
  const next = useCallback(() => setOpenIndex((i) => (i === items.length - 1 ? 0 : i + 1)), [items.length]);

  useEffect(() => {
    if (openIndex == null) return undefined;
    const onKey = (e) => {
      if (e.key === 'ArrowLeft') prev();
      if (e.key === 'ArrowRight') next();
      if (e.key === 'Escape') setOpenIndex(null);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [openIndex, prev, next]);

  const schema = {
    '@context': 'https://schema.org',
    '@type': 'ImageGallery',
    name: 'Ayodhya Photo Gallery',
    url: 'https://ayodhyadham.info/gallery',
    image: IMAGES.slice(0, 12).map((i) => `https://ayodhyadham.info${i.src}`),
  };

  return (
    <>
      <SEO
        title="Gallery"
        description="Photographs of the Ram Mandir, Hanuman Garhi, Kanak Bhawan and the sacred ghats of the Saryu in Ayodhya."
        canonicalUrl="/gallery"
        keywords="Ayodhya photos, Ram Mandir images, Ayodhya gallery, Saryu ghat photos"
        structuredData={schema}
      />
      <PageHero compact crumbs={[{ label: t('Home'), to: '/' }, { label: t('Gallery') }]} title={t('Gallery')} subtitle={t('Glimpses of the divine city')} />

      <div className="container content-wrap">
        <Tabs variant="pill" items={CATEGORIES.map((c) => ({ ...c, label: t(c.label) }))} value={category} onChange={(v) => { setCategory(v); setOpenIndex(null); }} ariaLabel={t('Gallery category')} />

        {items.length === 0 ? (
          <EmptyState icon="camera" title={t('No photos here yet')} body={t('Try another category.')} action={<Button variant="outline" onClick={() => setCategory('all')}>{t('View all')}</Button>} />
        ) : (
          <div className="gallery-grid" style={{ marginTop: 24 }}>
            {items.map((img, i) => (
              <button key={img.src} type="button" className="gallery-tile" onClick={() => setOpenIndex(i)}>
                <img src={img.src} alt={t(img.title)} loading="lazy" />
                <span className="gallery-tile__caption">
                  <Icon name="image" size={14} />{t(img.title)}
                </span>
              </button>
            ))}
          </div>
        )}
      </div>

      {openIndex != null && items[openIndex] && (
        <div className="lightbox" role="dialog" aria-modal="true" aria-label={t('Gallery')} onClick={() => setOpenIndex(null)}>
          <img src={items[openIndex].src} alt={t(items[openIndex].title)} onClick={(e) => e.stopPropagation()} />
          <button type="button" className="lightbox__close" aria-label={t('Close')} onClick={() => setOpenIndex(null)}><Icon name="x" size={20} /></button>
          <button type="button" className="lightbox__prev" aria-label={t('Previous')} onClick={(e) => { e.stopPropagation(); prev(); }}><Icon name="chevron-left" size={20} /></button>
          <button type="button" className="lightbox__next" aria-label={t('Next')} onClick={(e) => { e.stopPropagation(); next(); }}><Icon name="chevron-right" size={20} /></button>
          <div className="lightbox__counter">{t(items[openIndex].title)} · {openIndex + 1} / {items.length}</div>
        </div>
      )}
    </>
  );
}
